import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    HiOutlineChevronLeft, HiOutlineRefresh, HiOutlineSearch, HiOutlinePlus,
    HiOutlineDownload, HiOutlineTrash, HiOutlineVideoCamera, HiOutlineDocumentText,
    HiOutlineUpload, HiOutlineX, HiOutlinePlay, HiOutlinePencilAlt,
    HiOutlineChevronDown, HiOutlineChevronRight, HiOutlinePhotograph, HiOutlineEye,
    HiOutlineMenu, HiOutlineChevronUp, HiOutlineLink, HiOutlineExternalLink
} from 'react-icons/hi';
import axiosInstance from '../../api/axiosConfig';
import { useAuth } from '../../context/AuthContext';
import { hasCardAction } from '../../utils/permissionHelper';
import UserProfileButton from '../common/UserProfileButton';
import LogoutButton from '../common/LogoutButton';
import ProtectedImage from '../common/ProtectedImage';
import './WeeklyMeetings.css';

const API_URL = process.env.REACT_APP_API_URL || '';

const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

const meetingLabel = (m) => {
    if (!m) return '—';
    return m.weekLabel || m.title || formatDate(m.meetingDate);
};

// Upload dropdown shows the meeting name *and* its date, e.g. "week 3 — 25 Jul 2026".
const uploadOptionLabel = (m) => {
    if (!m) return '—';
    const name = m.weekLabel || m.title;
    const date = formatDate(m.meetingDate);
    return name && date ? `${name} — ${date}` : (name || date || '—');
};

const initials = (u) => (u?.fullName || u?.username || '?').slice(0, 2).toUpperCase();

// File-type presentation helpers (video / image / presentation / link).
const fileTypeLabel = (type) =>
    type === 'video' ? 'Video' : type === 'image' ? 'Image' : type === 'link' ? 'Link' : 'PPT';

const FileTypeIcon = ({ type, size = 18 }) => {
    if (type === 'video') return <HiOutlineVideoCamera size={size} />;
    if (type === 'image') return <HiOutlinePhotograph size={size} />;
    if (type === 'link') return <HiOutlineLink size={size} />;
    return <HiOutlineDocumentText size={size} />;
};

// Host of an attached link, shown as its "size"/meta (e.g. "canva.com").
const linkHost = (url) => {
    try { return new URL(url).hostname.replace(/^www\./, ''); } catch (e) { return url || ''; }
};

// Open an attached link in a new tab. noopener/noreferrer so the opened page
// can't reach back into this one.
const openLink = (file) => {
    if (file?.url) window.open(file.url, '_blank', 'noopener,noreferrer');
};

// "Open link" action button — the link equivalent of the download button.
const OpenLinkButton = ({ file }) => (
    <button className="wm-icon-btn" title={file.url} onClick={() => openLink(file)}>
        <HiOutlineExternalLink size={16} />
    </button>
);

// Download icon button that swaps to a circular progress ring (with %
// label) while a download is in flight. Used in both the member and
// approver file lists — works identically in the PWA shell and the browser
// tab since it's plain DOM/CSS with no platform-specific APIs.
const DownloadButton = ({ file, progress, onDownload }) => {
    const isDownloading = progress !== undefined;
    const radius = 14;
    const circumference = 2 * Math.PI * radius;
    return (
        <button
            className={`wm-icon-btn wm-download-btn ${isDownloading ? 'wm-downloading' : ''}`}
            title={isDownloading ? `Downloading… ${progress}%` : 'Download'}
            onClick={() => onDownload(file)}
            disabled={isDownloading}
        >
            {isDownloading ? (
                <span className="wm-progress-ring" aria-label={`Downloading ${progress}%`}>
                    <svg width="32" height="32" viewBox="0 0 32 32">
                        <circle className="wm-progress-ring-bg" cx="16" cy="16" r={radius} />
                        <circle
                            className="wm-progress-ring-fill"
                            cx="16" cy="16" r={radius}
                            strokeDasharray={circumference}
                            strokeDashoffset={circumference * (1 - progress / 100)}
                        />
                    </svg>
                    <span className="wm-progress-ring-label">{progress}</span>
                </span>
            ) : (
                <HiOutlineDownload size={16} />
            )}
        </button>
    );
};

// Editable play-order position. Typing a number and pressing Enter/blurring
// jumps the file straight to that slot — every file in between shifts to
// make room (an insert, not a swap). Keeps its own draft value so the field
// doesn't fight the user's keystrokes; it resyncs once the server-confirmed
// position comes back down as `position`.
const OrderInput = ({ position, max, onMove }) => {
    const [value, setValue] = useState(String(position));

    useEffect(() => { setValue(String(position)); }, [position]);

    const commit = () => {
        const num = parseInt(value, 10);
        if (!Number.isFinite(num)) { setValue(String(position)); return; }
        const clamped = Math.min(Math.max(num, 1), max);
        setValue(String(clamped));
        if (clamped !== position) onMove(clamped);
    };

    return (
        <input
            type="number"
            className="wm-order-input"
            min={1}
            max={max}
            value={value}
            draggable={false}
            onChange={(e) => setValue(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); } }}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            title="Type a position and press Enter to move this file there"
        />
    );
};

// User avatar: shows the profile photo when available, falls back to initials.
const Avatar = ({ user }) => (
    <ProtectedImage
        src={user?.profilePhoto}
        alt={user?.fullName || user?.username || 'User'}
        className="wm-avatar-img"
        fallback={<span className="wm-avatar">{initials(user)}</span>}
    />
);

const WeeklyMeetings = () => {
    const navigate = useNavigate();
    const { user, token } = useAuth();

    const userPermissions = user?.permissions || { cards: [], actions: [] };
    const isApprover = user?.role === 'system_admin'
        || hasCardAction(userPermissions, 'weekly-meetings', 'approve');
    const canDelete = user?.role === 'system_admin'
        || hasCardAction(userPermissions, 'weekly-meetings', 'delete');

    const [activeTab, setActiveTab] = useState(isApprover ? 'submissions' : 'mine');
    const [loading, setLoading] = useState(true);
    const [meetings, setMeetings] = useState([]);

    // Member state
    const [myFiles, setMyFiles] = useState([]);
    const [myPoints, setMyPoints] = useState([]);
    const [myTotal, setMyTotal] = useState(0);

    // Upload state
    const [uploadMeetingId, setUploadMeetingId] = useState('');
    const [uploadFiles, setUploadFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0); // 0–100

    // Link-attach state (for members whose presentation isn't a file)
    const [linkUrl, setLinkUrl] = useState('');
    const [linkTitle, setLinkTitle] = useState('');
    const [addingLink, setAddingLink] = useState(false);

    // Approver state
    const [allFiles, setAllFiles] = useState([]);
    const [search, setSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [meetingFilter, setMeetingFilter] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [dragIndex, setDragIndex] = useState(null);

    // Modals
    const [pointModal, setPointModal] = useState(null); // { userId, userName }
    const [showMeetingForm, setShowMeetingForm] = useState(false);
    const [videoPreview, setVideoPreview] = useState(null); // file being played

    // Download progress per file id (0–100 while in flight; absent when idle)
    const [downloads, setDownloads] = useState({});

    const [error, setError] = useState('');

    // Inline streaming URL — the <video> tag can't send the auth header, so the
    // JWT travels as a query param (the stream endpoint accepts it there).
    const streamUrl = useCallback((file) =>
        `${API_URL}/api/meetings/files/${file._id}/stream?token=${encodeURIComponent(token || '')}`,
    [token]);

    const loadMeetings = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/api/meetings');
            setMeetings(res.data.meetings || []);
        } catch (e) {
            console.error('Error loading meetings:', e);
        }
    }, []);

    const loadMine = useCallback(async () => {
        try {
            const [filesRes, pointsRes] = await Promise.all([
                axiosInstance.get('/api/meetings/files/mine'),
                axiosInstance.get('/api/meetings/points/mine')
            ]);
            setMyFiles(filesRes.data.files || []);
            setMyPoints(pointsRes.data.points || []);
            setMyTotal(pointsRes.data.total || 0);
        } catch (e) {
            console.error('Error loading my data:', e);
        }
    }, []);

    const loadAllFiles = useCallback(async () => {
        try {
            const params = new URLSearchParams();
            if (search.trim()) params.append('q', search.trim());
            if (typeFilter) params.append('type', typeFilter);
            if (meetingFilter) params.append('meetingId', meetingFilter);
            params.append('page', String(page));
            // Viewing one week enables drag-reorder — pull the whole playlist onto
            // a single page so files can be dragged across the full sequence.
            if (meetingFilter) params.append('limit', '100');
            const res = await axiosInstance.get(`/api/meetings/files?${params.toString()}`);
            setAllFiles(res.data.files || []);
            setTotalPages(res.data.totalPages || 1);
        } catch (e) {
            console.error('Error loading all files:', e);
        }
    }, [search, typeFilter, meetingFilter, page]);

    // Initial load. Kept deliberately light: meetings + the user's own data.
    // The submissions list is owned by the filter effect below, and the points
    // leaderboard is loaded lazily by PointsView only when that tab is opened —
    // so nothing here scales with the total number of files or awards.
    useEffect(() => {
        const init = async () => {
            setLoading(true);
            await Promise.all([loadMeetings(), loadMine()]);
            setLoading(false);
        };
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Owns the submissions list: initial load + re-fetch on filter/page change.
    useEffect(() => {
        if (isApprover) {
            loadAllFiles();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, typeFilter, meetingFilter, page]);

    // Warn before closing/refreshing the tab while an upload is running — the
    // upload can't survive the tab closing, so this prevents accidental loss.
    useEffect(() => {
        if (!uploading) return;
        const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [uploading]);

    // Drag-reorder is only meaningful when a single week is selected (the
    // submissions list is then the meeting's play order, first-to-last).
    const reorderEnabled = !!meetingFilter;

    // Persist a reordered list (shared by drag-drop and the up/down arrows).
    const persistOrder = async (reordered) => {
        setAllFiles(reordered); // optimistic — reflect the new order immediately
        try {
            await axiosInstance.put(`/api/meetings/${meetingFilter}/files/order`, {
                orderedIds: reordered.map((f) => f._id)
            });
        } catch (e) {
            console.error('Reorder failed:', e);
            setError('Could not save the new play order');
            loadAllFiles(); // revert to server truth
        }
    };

    const handleReorderDrop = (toIndex) => {
        if (dragIndex === null || dragIndex === toIndex) { setDragIndex(null); return; }
        const reordered = [...allFiles];
        const [moved] = reordered.splice(dragIndex, 1);
        reordered.splice(toIndex, 0, moved);
        setDragIndex(null);
        persistOrder(reordered);
    };

    // Swap a row with its neighbour (up = -1, down = +1).
    const moveRow = (index, dir) => {
        const target = index + dir;
        if (target < 0 || target >= allFiles.length) return;
        const reordered = [...allFiles];
        [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
        persistOrder(reordered);
    };

    // Jump a row straight to a typed position — pulls it out and reinserts it
    // there, shifting everything in between (not a swap).
    const moveToPosition = (index, newPosition) => {
        const targetIndex = newPosition - 1;
        if (targetIndex === index) return;
        const reordered = [...allFiles];
        const [moved] = reordered.splice(index, 1);
        reordered.splice(targetIndex, 0, moved);
        persistOrder(reordered);
    };

    const handleDownload = async (file) => {
        if (downloads[file._id] !== undefined) return; // already downloading
        setDownloads((d) => ({ ...d, [file._id]: 0 }));
        try {
            const res = await axiosInstance.get(`/api/meetings/files/${file._id}/download?download=1`, {
                responseType: 'blob',
                onDownloadProgress: (evt) => {
                    if (!evt.total) return;
                    setDownloads((d) => ({ ...d, [file._id]: Math.round((evt.loaded / evt.total) * 100) }));
                }
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = file.originalName || 'download';
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            console.error('Download failed:', e);
            setError('Could not download file');
        } finally {
            setDownloads((d) => {
                const next = { ...d };
                delete next[file._id];
                return next;
            });
        }
    };

    const handleDeleteFile = async (file, refresh) => {
        if (!window.confirm(`Delete "${file.originalName}"? This cannot be undone.`)) return;
        try {
            await axiosInstance.delete(`/api/meetings/files/${file._id}`);
            refresh();
        } catch (e) {
            console.error('Delete failed:', e);
            setError(e.response?.data?.message || 'Could not delete file');
        }
    };

    const handleUpload = async (e) => {
        e.preventDefault();
        setError('');
        if (!uploadMeetingId) { setError('Select a meeting first'); return; }
        if (!uploadFiles.length) { setError('Choose at least one file'); return; }
        const form = new FormData();
        uploadFiles.forEach((f) => form.append('files', f));
        try {
            setUploading(true);
            setUploadProgress(0);
            await axiosInstance.post(`/api/meetings/${uploadMeetingId}/files`, form, {
                headers: { 'Content-Type': 'multipart/form-data' },
                timeout: 45 * 60 * 1000,
                onUploadProgress: (evt) => {
                    if (!evt.total) return;
                    setUploadProgress(Math.round((evt.loaded / evt.total) * 100));
                }
            });
            setUploadFiles([]);
            setUploadMeetingId('');
            await loadMine();
        } catch (e) {
            console.error('Upload failed:', e);
            setError(e.response?.data?.message || 'Upload failed');
        } finally {
            setUploading(false);
            setUploadProgress(0);
        }
    };

    // Attach an external presentation link (Canva / Google Slides / YouTube…)
    // instead of uploading a file. Same meeting selection and upload window as
    // a file upload; it just posts a URL.
    const handleAddLink = async (e) => {
        e.preventDefault();
        setError('');
        if (!uploadMeetingId) { setError('Select a meeting first'); return; }
        const url = linkUrl.trim();
        if (!url) { setError('Paste a link first'); return; }
        try {
            setAddingLink(true);
            await axiosInstance.post(`/api/meetings/${uploadMeetingId}/links`, {
                url,
                title: linkTitle.trim()
            });
            setLinkUrl('');
            setLinkTitle('');
            await loadMine();
        } catch (e) {
            console.error('Attach link failed:', e);
            setError(e.response?.data?.message || 'Could not attach link');
        } finally {
            setAddingLink(false);
        }
    };

    if (loading) {
        return (
            <div className="wm-shell">
                <div className="wm-loading">Loading…</div>
            </div>
        );
    }

    return (
        <div className="wm-shell">
            <header className="wm-header">
                <div className="wm-header-left">
                    <button
                        className="wm-back-btn"
                        onClick={() => {
                            if (uploading && !window.confirm('An upload is still in progress and will be cancelled if you leave. Leave anyway?')) return;
                            navigate('/purpose-selection');
                        }}
                        aria-label="Back"
                    >
                        <HiOutlineChevronLeft size={20} />
                    </button>
                    <div className="wm-header-title">
                        <h1>Weekly Meetings</h1>
                        <span>Module</span>
                    </div>
                </div>
                <div className="wm-header-right">
                    <div className="wm-tabs">
                        {!isApprover && (
                            <button className={`wm-tab ${activeTab === 'mine' ? 'active' : ''}`} onClick={() => setActiveTab('mine')}>
                                My uploads
                            </button>
                        )}
                        {isApprover && (
                            <>
                                <button className={`wm-tab ${activeTab === 'submissions' ? 'active' : ''}`} onClick={() => setActiveTab('submissions')}>
                                    All submissions
                                </button>
                                <button className={`wm-tab ${activeTab === 'points' ? 'active' : ''}`} onClick={() => setActiveTab('points')}>
                                    Points
                                </button>
                                <button className={`wm-tab ${activeTab === 'meetings' ? 'active' : ''}`} onClick={() => setActiveTab('meetings')}>
                                    Meetings
                                </button>
                                <button className={`wm-tab ${activeTab === 'mine' ? 'active' : ''}`} onClick={() => setActiveTab('mine')}>
                                    My uploads
                                </button>
                            </>
                        )}
                    </div>
                    <span className={`wm-role-badge ${isApprover ? 'approver' : 'member'}`}>
                        {isApprover ? 'Approver' : 'Member'}
                    </span>
                    <UserProfileButton />
                    <LogoutButton variant="secondary" />
                </div>
            </header>

            <div className="wm-content">

            {error && (
                <div className="wm-error" onClick={() => setError('')}>
                    {error} <HiOutlineX size={14} />
                </div>
            )}

            {/* ---- Member / My uploads ---- */}
            {activeTab === 'mine' && (
                <MemberView
                    meetings={meetings}
                    myFiles={myFiles}
                    myPoints={myPoints}
                    myTotal={myTotal}
                    uploadMeetingId={uploadMeetingId}
                    setUploadMeetingId={setUploadMeetingId}
                    uploadFiles={uploadFiles}
                    setUploadFiles={setUploadFiles}
                    uploading={uploading}
                    uploadProgress={uploadProgress}
                    onUpload={handleUpload}
                    linkUrl={linkUrl}
                    setLinkUrl={setLinkUrl}
                    linkTitle={linkTitle}
                    setLinkTitle={setLinkTitle}
                    addingLink={addingLink}
                    onAddLink={handleAddLink}
                    onDownload={handleDownload}
                    downloads={downloads}
                    onDelete={(f) => handleDeleteFile(f, loadMine)}
                    onPlay={setVideoPreview}
                />
            )}

            {/* ---- Approver: All submissions ---- */}
            {isApprover && activeTab === 'submissions' && (
                <section className="wm-section">
                    <div className="wm-toolbar">
                        <div className="wm-search">
                            <HiOutlineSearch size={16} />
                            <input
                                type="text"
                                placeholder="Search by user name or email"
                                value={search}
                                onChange={(e) => { setPage(1); setSearch(e.target.value); }}
                            />
                        </div>
                        <select value={meetingFilter} onChange={(e) => { setPage(1); setMeetingFilter(e.target.value); }}>
                            <option value="">All weeks</option>
                            {meetings.map((m) => <option key={m._id} value={m._id}>{meetingLabel(m)}</option>)}
                        </select>
                        <select value={typeFilter} onChange={(e) => { setPage(1); setTypeFilter(e.target.value); }}>
                            <option value="">All types</option>
                            <option value="video">Video</option>
                            <option value="presentation">Presentation</option>
                            <option value="image">Image</option>
                            <option value="link">Link</option>
                        </select>
                        <button className="wm-icon-btn" onClick={loadAllFiles} title="Refresh">
                            <HiOutlineRefresh size={16} />
                        </button>
                    </div>

                    {reorderEnabled ? (
                        <p className="wm-hint">Set the play order for this meeting — type a position number, drag rows, or use the ↑/↓ arrows. The top row plays first.</p>
                    ) : (
                        <p className="wm-hint">Pick a week above to arrange the file play order (add/swap the order files are played in).</p>
                    )}

                    <div className="wm-table-wrap">
                        <table className="wm-table">
                            <thead>
                                <tr>
                                    {reorderEnabled && <th style={{ width: '116px' }}>Order</th>}
                                    <th>User</th>
                                    <th>Week</th>
                                    <th>File</th>
                                    <th>Type</th>
                                    <th>Size</th>
                                    <th>Uploaded</th>
                                    <th className="wm-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {allFiles.length === 0 && (
                                    <tr><td colSpan={reorderEnabled ? 8 : 7} className="wm-empty">No submissions found</td></tr>
                                )}
                                {allFiles.map((f, i) => (
                                    <tr
                                        key={f._id}
                                        className={reorderEnabled ? `wm-draggable ${dragIndex === i ? 'wm-dragging' : ''}` : ''}
                                        draggable={reorderEnabled}
                                        onDragStart={reorderEnabled ? () => setDragIndex(i) : undefined}
                                        onDragOver={reorderEnabled ? (e) => e.preventDefault() : undefined}
                                        onDrop={reorderEnabled ? () => handleReorderDrop(i) : undefined}
                                        onDragEnd={reorderEnabled ? () => setDragIndex(null) : undefined}
                                    >
                                        {reorderEnabled && (
                                            <td className="wm-drag-cell">
                                                <HiOutlineMenu size={16} className="wm-drag-handle" title="Drag to reorder" />
                                                <OrderInput position={i + 1} max={allFiles.length} onMove={(pos) => moveToPosition(i, pos)} />
                                                <span className="wm-order-btns">
                                                    <button
                                                        type="button"
                                                        className="wm-order-btn"
                                                        title="Move up"
                                                        disabled={i === 0}
                                                        onClick={() => moveRow(i, -1)}
                                                    >
                                                        <HiOutlineChevronUp size={14} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="wm-order-btn"
                                                        title="Move down"
                                                        disabled={i === allFiles.length - 1}
                                                        onClick={() => moveRow(i, 1)}
                                                    >
                                                        <HiOutlineChevronDown size={14} />
                                                    </button>
                                                </span>
                                            </td>
                                        )}
                                        <td>
                                            <div className="wm-user">
                                                <Avatar user={f.user} />
                                                <div>
                                                    <div className="wm-user-name">{f.user?.fullName || f.user?.username || 'Unknown'}</div>
                                                    <div className="wm-user-email">{f.user?.email || ''}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td>{meetingLabel(f.meeting)}</td>
                                        <td className="wm-fname">
                                            {f.type === 'link' ? (
                                                <a href={f.url} target="_blank" rel="noopener noreferrer" className="wm-link" title={f.url}>
                                                    {f.originalName}
                                                </a>
                                            ) : f.originalName}
                                        </td>
                                        <td>
                                            <span className={`wm-pill ${f.type}`}>
                                                <FileTypeIcon type={f.type} size={13} />
                                                {fileTypeLabel(f.type)}
                                            </span>
                                        </td>
                                        <td>{f.type === 'link' ? linkHost(f.url) : formatBytes(f.sizeBytes)}</td>
                                        <td>{formatDate(f.createdAt)}</td>
                                        <td className="wm-right">
                                            {f.type === 'video' && (
                                                <button className="wm-icon-btn" title="Play" onClick={() => setVideoPreview(f)}>
                                                    <HiOutlinePlay size={16} />
                                                </button>
                                            )}
                                            {f.type === 'image' && (
                                                <button className="wm-icon-btn" title="View" onClick={() => setVideoPreview(f)}>
                                                    <HiOutlineEye size={16} />
                                                </button>
                                            )}
                                            {f.type === 'link'
                                                ? <OpenLinkButton file={f} />
                                                : <DownloadButton file={f} progress={downloads[f._id]} onDownload={handleDownload} />}
                                            <button
                                                className="wm-btn-sm"
                                                onClick={() => setPointModal({ userId: f.user?._id, userName: f.user?.fullName || f.user?.username, meetingId: f.meeting?._id })}
                                            >
                                                <HiOutlinePlus size={14} /> Points
                                            </button>
                                            {canDelete && (
                                                <button className="wm-icon-btn danger" title="Delete" onClick={() => handleDeleteFile(f, loadAllFiles)}>
                                                    <HiOutlineTrash size={16} />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {totalPages > 1 && (
                        <div className="wm-pagination">
                            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</button>
                            <span>Page {page} of {totalPages}</span>
                            <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
                        </div>
                    )}
                </section>
            )}

            {/* ---- Approver: Points (leaderboard + detail) ---- */}
            {isApprover && activeTab === 'points' && (
                <PointsView
                    meetings={meetings}
                    onError={setError}
                />
            )}

            {/* ---- Approver: Meetings ---- */}
            {isApprover && activeTab === 'meetings' && (
                <MeetingsManager
                    meetings={meetings}
                    showForm={showMeetingForm}
                    setShowForm={setShowMeetingForm}
                    onChange={loadMeetings}
                    canDelete={canDelete}
                />
            )}

            </div>{/* /wm-content */}

            {/* ---- Award points modal ---- */}
            {pointModal && (
                <PointModal
                    target={pointModal}
                    meetings={meetings}
                    onClose={() => setPointModal(null)}
                    onSaved={() => setPointModal(null)}
                    onError={setError}
                />
            )}

            {/* ---- Media preview modal (video or image) ---- */}
            {videoPreview && (
                <div className="wm-modal-overlay" onClick={() => setVideoPreview(null)}>
                    <div className="wm-video-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="wm-modal-head">
                            <h3>{videoPreview.originalName}</h3>
                            <button className="wm-icon-btn" onClick={() => setVideoPreview(null)}><HiOutlineX size={18} /></button>
                        </div>
                        {videoPreview.type === 'image' ? (
                            <img src={streamUrl(videoPreview)} alt={videoPreview.originalName} className="wm-video" />
                        ) : (
                            <video src={streamUrl(videoPreview)} controls autoPlay className="wm-video" />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// ---------- Member view ----------
const MemberView = ({
    meetings, myFiles, myPoints, myTotal,
    uploadMeetingId, setUploadMeetingId, uploadFiles, setUploadFiles,
    uploading, uploadProgress, onUpload,
    linkUrl, setLinkUrl, linkTitle, setLinkTitle, addingLink, onAddLink,
    onDownload, downloads, onDelete, onPlay
}) => (
    <section className="wm-section">
        <div className="wm-stats">
            <div className="wm-stat">
                <span className="wm-stat-label">My total points</span>
                <span className="wm-stat-value">{myTotal}</span>
            </div>
            <div className="wm-stat">
                <span className="wm-stat-label">My submissions</span>
                <span className="wm-stat-value">{myFiles.length}</span>
            </div>
            <div className="wm-stat">
                <span className="wm-stat-label">Open meetings</span>
                <span className="wm-stat-value">{meetings.filter((m) => m.uploadOpen).length}</span>
            </div>
        </div>

        <div className="wm-card">
            <h3>Submit for a meeting</h3>
            {/* The meeting select is shared: pick the week once, then either
                upload files or attach a link. */}
            <div className="wm-form-row">
                <select value={uploadMeetingId} onChange={(e) => setUploadMeetingId(e.target.value)}>
                    <option value="">Select a meeting…</option>
                    {meetings.filter((m) => m.uploadOpen).map((m) => <option key={m._id} value={m._id}>{uploadOptionLabel(m)}</option>)}
                </select>
            </div>
            <form onSubmit={onUpload}>
                <label className={`wm-dropzone ${uploading ? 'wm-dropzone-disabled' : ''}`}>
                    <HiOutlineUpload size={24} />
                    <span>{uploadFiles.length ? `${uploadFiles.length} file(s) selected` : 'Choose video (MP4/MOV/WEBM), presentation (PPT/PPTX/PDF) or image (JPG/PNG/GIF/WEBP)'}</span>
                    <small>Max 1 GB per file</small>
                    <input
                        type="file"
                        multiple
                        accept=".mp4,.mov,.webm,.ppt,.pptx,.pdf,.jpg,.jpeg,.png,.gif,.webp"
                        onChange={(e) => setUploadFiles(Array.from(e.target.files))}
                        disabled={uploading}
                        hidden
                    />
                </label>
                {uploadFiles.length > 0 && (
                    <ul className="wm-pending">
                        {uploadFiles.map((f, i) => (
                            <li key={i}>{f.name} <span>{formatBytes(f.size)}</span></li>
                        ))}
                    </ul>
                )}
                {uploading && (
                    <div className="wm-progress" role="progressbar" aria-valuenow={uploadProgress} aria-valuemin={0} aria-valuemax={100}>
                        <div className="wm-progress-track">
                            <div className="wm-progress-fill" style={{ width: `${uploadProgress}%` }} />
                        </div>
                        <span className="wm-progress-label">
                            {uploadProgress < 100 ? `Uploading… ${uploadProgress}%` : 'Finishing up…'}
                        </span>
                    </div>
                )}
                <button type="submit" className="wm-btn-primary" disabled={uploading}>
                    {uploading
                        ? (uploadProgress < 100 ? `Uploading ${uploadProgress}%` : 'Finishing…')
                        : 'Upload'}
                </button>
            </form>

            <div className="wm-or"><span>or</span></div>

            {/* For members whose presentation isn't a file — Canva, Google
                Slides, YouTube, SharePoint… — attach the link instead. */}
            <form className="wm-link-form" onSubmit={onAddLink}>
                <label className="wm-label">Presentation link</label>
                <div className="wm-link-input">
                    <HiOutlineLink size={16} />
                    <input
                        type="url"
                        value={linkUrl}
                        onChange={(e) => setLinkUrl(e.target.value)}
                        placeholder="https://www.canva.com/design/…"
                        disabled={addingLink}
                    />
                </div>
                <label className="wm-label">Title (optional)</label>
                <input
                    type="text"
                    value={linkTitle}
                    onChange={(e) => setLinkTitle(e.target.value)}
                    placeholder="What the link opens, e.g. Q3 sales deck"
                    disabled={addingLink}
                />
                <button type="submit" className="wm-btn-secondary wm-attach-btn" disabled={addingLink}>
                    <HiOutlineLink size={16} /> {addingLink ? 'Attaching…' : 'Attach link'}
                </button>
            </form>

            {meetings.length === 0 ? (
                <p className="wm-hint">No meetings available yet. An approver needs to create one first.</p>
            ) : !meetings.some((m) => m.uploadOpen) && (
                <p className="wm-hint">No meetings are open for submissions right now.</p>
            )}
        </div>

        <div className="wm-card">
            <h3>My submissions</h3>
            {myFiles.length === 0 ? (
                <p className="wm-hint">You haven't uploaded a file or attached a link yet.</p>
            ) : (
                <div className="wm-file-list">
                    {myFiles.map((f) => (
                        <div className="wm-file-row" key={f._id}>
                            <FileTypeIcon type={f.type} size={18} />
                            <div className="wm-file-info">
                                <span className="wm-file-name">{f.originalName}</span>
                                <span className="wm-file-meta">
                                    {meetingLabel(f.meeting)} · {f.type === 'link' ? linkHost(f.url) : formatBytes(f.sizeBytes)}
                                </span>
                            </div>
                            {f.type === 'video' && (
                                <button className="wm-icon-btn" title="Play" onClick={() => onPlay(f)}>
                                    <HiOutlinePlay size={16} />
                                </button>
                            )}
                            {f.type === 'image' && (
                                <button className="wm-icon-btn" title="View" onClick={() => onPlay(f)}>
                                    <HiOutlineEye size={16} />
                                </button>
                            )}
                            {f.type === 'link'
                                ? <OpenLinkButton file={f} />
                                : <DownloadButton file={f} progress={downloads[f._id]} onDownload={onDownload} />}
                            <button className="wm-icon-btn danger" title="Delete" onClick={() => onDelete(f)}>
                                <HiOutlineTrash size={16} />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>

        <div className="wm-card">
            <h3>My points</h3>
            {myPoints.length === 0 ? (
                <p className="wm-hint">No points awarded yet.</p>
            ) : (
                <div className="wm-table-wrap">
                    <table className="wm-table">
                        <thead>
                            <tr><th>Week</th><th>Points</th><th>Note</th><th>Date</th></tr>
                        </thead>
                        <tbody>
                            {myPoints.map((p) => (
                                <tr key={p._id}>
                                    <td>{meetingLabel(p.meeting)}</td>
                                    <td className="wm-points">{p.points}</td>
                                    <td>{p.note || '—'}</td>
                                    <td>{formatDate(p.createdAt)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    </section>
);

// ---------- Award points modal ----------
const PointModal = ({ target, meetings, onClose, onSaved, onError }) => {
    const [points, setPoints] = useState('10');
    const [meetingId, setMeetingId] = useState(target.meetingId || '');
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        const num = Number(points);
        if (!Number.isFinite(num)) { onError('Points must be a number'); return; }
        try {
            setSaving(true);
            await axiosInstance.post('/api/meetings/points', {
                userId: target.userId,
                meetingId: meetingId || undefined,
                points: num,
                note: note.trim()
            });
            onSaved();
        } catch (e) {
            onError(e.response?.data?.message || 'Could not award points');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="wm-modal-overlay" onClick={onClose}>
            <div className="wm-modal" onClick={(e) => e.stopPropagation()}>
                <div className="wm-modal-head">
                    <h3>Award points to {target.userName}</h3>
                    <button className="wm-icon-btn" onClick={onClose}><HiOutlineX size={18} /></button>
                </div>
                <form onSubmit={submit}>
                    <label className="wm-label">Meeting</label>
                    <select value={meetingId} onChange={(e) => setMeetingId(e.target.value)}>
                        <option value="">No specific meeting</option>
                        {meetings.map((m) => <option key={m._id} value={m._id}>{meetingLabel(m)}</option>)}
                    </select>
                    <label className="wm-label">Points</label>
                    <input type="number" value={points} onChange={(e) => setPoints(e.target.value)} />
                    <label className="wm-label">Note (optional)</label>
                    <input type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason / criteria" />
                    <div className="wm-modal-actions">
                        <button type="button" className="wm-btn-secondary" onClick={onClose}>Cancel</button>
                        <button type="submit" className="wm-btn-primary" disabled={saving}>
                            {saving ? 'Saving…' : 'Award points'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// ---------- Meetings manager ----------
const MeetingsManager = ({ meetings, showForm, setShowForm, onChange, canDelete }) => {
    const [title, setTitle] = useState('');
    const [weekLabel, setWeekLabel] = useState('');
    const [meetingDate, setMeetingDate] = useState('');
    const [description, setDescription] = useState('');
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');

    const create = async (e) => {
        e.preventDefault();
        setErr('');
        if (!title.trim() || !meetingDate) { setErr('Title and date are required'); return; }
        try {
            setSaving(true);
            await axiosInstance.post('/api/meetings', { title: title.trim(), weekLabel: weekLabel.trim(), meetingDate, description: description.trim() });
            setTitle(''); setWeekLabel(''); setMeetingDate(''); setDescription('');
            setShowForm(false);
            onChange();
        } catch (e) {
            setErr(e.response?.data?.message || 'Could not create meeting');
        } finally {
            setSaving(false);
        }
    };

    const remove = async (m) => {
        if (!window.confirm(`Delete meeting "${meetingLabel(m)}"?`)) return;
        try {
            await axiosInstance.delete(`/api/meetings/${m._id}`);
            onChange();
        } catch (e) {
            setErr(e.response?.data?.message || 'Could not delete meeting');
        }
    };

    return (
        <section className="wm-section">
            <div className="wm-card">
                <div className="wm-card-head">
                    <h3>Meetings</h3>
                    <button className="wm-btn-primary" onClick={() => setShowForm((s) => !s)}>
                        <HiOutlinePlus size={16} /> New meeting
                    </button>
                </div>
                {err && <div className="wm-error">{err}</div>}
                {showForm && (
                    <form className="wm-meeting-form" onSubmit={create}>
                        <div className="wm-form-grid">
                            <div>
                                <label className="wm-label">Title</label>
                                <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Weekly review" />
                            </div>
                            <div>
                                <label className="wm-label">Week label</label>
                                <input type="text" value={weekLabel} onChange={(e) => setWeekLabel(e.target.value)} placeholder="Week 26 — 2026" />
                            </div>
                            <div>
                                <label className="wm-label">Meeting date</label>
                                <input type="date" value={meetingDate} onChange={(e) => setMeetingDate(e.target.value)} />
                            </div>
                        </div>
                        <label className="wm-label">Description (optional)</label>
                        <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} />
                        <div className="wm-modal-actions">
                            <button type="submit" className="wm-btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Create'}</button>
                        </div>
                    </form>
                )}

                <div className="wm-table-wrap">
                    <table className="wm-table">
                        <thead>
                            <tr><th>Week</th><th>Title</th><th>Date</th>{canDelete && <th className="wm-right">Actions</th>}</tr>
                        </thead>
                        <tbody>
                            {meetings.length === 0 && (
                                <tr><td colSpan={canDelete ? 4 : 3} className="wm-empty">No meetings yet</td></tr>
                            )}
                            {meetings.map((m) => (
                                <tr key={m._id}>
                                    <td>{m.weekLabel || '—'}</td>
                                    <td>{m.title}</td>
                                    <td>{formatDate(m.meetingDate)}</td>
                                    {canDelete && (
                                        <td className="wm-right">
                                            <button className="wm-icon-btn danger" title="Delete" onClick={() => remove(m)}>
                                                <HiOutlineTrash size={16} />
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </section>
    );
};

// ---------- Points leaderboard + detail ----------
// Totals come pre-aggregated from the server (one row per user). A user's
// individual awards are fetched lazily and paginated only when their row is
// expanded — so this tab stays fast no matter how many awards exist.
const PointsView = ({ meetings, onError }) => {
    const [leaderboard, setLeaderboard] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(null);      // userId
    const [detail, setDetail] = useState({});            // userId -> { entries, page, totalPages, loading }
    const [editEntry, setEditEntry] = useState(null);

    const loadLeaderboard = useCallback(async () => {
        try {
            setLoading(true);
            const res = await axiosInstance.get('/api/meetings/points/leaderboard');
            setLeaderboard(res.data.leaderboard || []);
        } catch (e) {
            onError('Could not load points leaderboard');
        } finally {
            setLoading(false);
        }
    }, [onError]);

    useEffect(() => { loadLeaderboard(); }, [loadLeaderboard]);

    const loadDetail = useCallback(async (userId, page = 1) => {
        setDetail((d) => ({ ...d, [userId]: { ...(d[userId] || {}), loading: true } }));
        try {
            const res = await axiosInstance.get(`/api/meetings/points?userId=${userId}&page=${page}`);
            setDetail((d) => ({
                ...d,
                [userId]: {
                    entries: res.data.points || [],
                    page: res.data.page || 1,
                    totalPages: res.data.totalPages || 1,
                    loading: false
                }
            }));
        } catch (e) {
            onError('Could not load point details');
            setDetail((d) => ({ ...d, [userId]: { ...(d[userId] || {}), loading: false } }));
        }
    }, [onError]);

    const toggle = (userId) => {
        if (expanded === userId) { setExpanded(null); return; }
        setExpanded(userId);
        if (!detail[userId]) loadDetail(userId, 1);
    };

    // After a change, refresh both the totals and the currently open detail page.
    const refresh = () => {
        loadLeaderboard();
        if (expanded) loadDetail(expanded, detail[expanded]?.page || 1);
    };

    const handleDelete = async (entry) => {
        if (!window.confirm('Delete this point entry?')) return;
        try {
            await axiosInstance.delete(`/api/meetings/points/${entry._id}`);
            refresh();
        } catch (e) {
            onError(e.response?.data?.message || 'Could not delete point entry');
        }
    };

    if (loading) {
        return <section className="wm-section"><p className="wm-hint">Loading…</p></section>;
    }

    return (
        <section className="wm-section">
            <div className="wm-table-wrap">
                <table className="wm-table">
                    <thead>
                        <tr>
                            <th style={{ width: '40px' }}></th>
                            <th>User</th>
                            <th>Total points</th>
                            <th>Awards</th>
                            <th>Last awarded</th>
                        </tr>
                    </thead>
                    <tbody>
                        {leaderboard.length === 0 && (
                            <tr><td colSpan={5} className="wm-empty">No points awarded yet</td></tr>
                        )}
                        {leaderboard.map((row) => {
                            const id = row.user?._id || 'unknown';
                            const isOpen = expanded === id;
                            const d = detail[id];
                            return (
                                <React.Fragment key={id}>
                                    <tr className="wm-lb-row" onClick={() => toggle(id)}>
                                        <td>{isOpen ? <HiOutlineChevronDown size={16} /> : <HiOutlineChevronRight size={16} />}</td>
                                        <td>
                                            <div className="wm-user">
                                                <Avatar user={row.user} />
                                                <div>
                                                    <div className="wm-user-name">{row.user?.fullName || row.user?.username || 'Unknown'}</div>
                                                    <div className="wm-user-email">{row.user?.email || ''}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="wm-points">{row.total}</td>
                                        <td>{row.count}</td>
                                        <td>{formatDate(row.last)}</td>
                                    </tr>
                                    {isOpen && (
                                        <tr className="wm-detail-row">
                                            <td></td>
                                            <td colSpan={4}>
                                                {d?.loading && !d?.entries ? (
                                                    <p className="wm-hint">Loading…</p>
                                                ) : (
                                                    <>
                                                        <table className="wm-subtable">
                                                            <thead>
                                                                <tr><th>Week</th><th>Points</th><th>Note</th><th>Awarded by</th><th>Date</th><th className="wm-right">Actions</th></tr>
                                                            </thead>
                                                            <tbody>
                                                                {(d?.entries || []).map((p) => (
                                                                    <tr key={p._id}>
                                                                        <td>{meetingLabel(p.meeting)}</td>
                                                                        <td className="wm-points">{p.points}</td>
                                                                        <td>{p.note || '—'}</td>
                                                                        <td>{p.awardedBy?.fullName || p.awardedBy?.username || '—'}</td>
                                                                        <td>{formatDate(p.createdAt)}</td>
                                                                        <td className="wm-right">
                                                                            <button className="wm-icon-btn" title="Edit" onClick={() => setEditEntry(p)}>
                                                                                <HiOutlinePencilAlt size={16} />
                                                                            </button>
                                                                            <button className="wm-icon-btn danger" title="Delete" onClick={() => handleDelete(p)}>
                                                                                <HiOutlineTrash size={16} />
                                                                            </button>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                        {d && d.totalPages > 1 && (
                                                            <div className="wm-pagination">
                                                                <button disabled={d.page <= 1} onClick={() => loadDetail(id, d.page - 1)}>Prev</button>
                                                                <span>Page {d.page} of {d.totalPages}</span>
                                                                <button disabled={d.page >= d.totalPages} onClick={() => loadDetail(id, d.page + 1)}>Next</button>
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {editEntry && (
                <EditPointModal
                    entry={editEntry}
                    meetings={meetings}
                    onClose={() => setEditEntry(null)}
                    onSaved={() => { setEditEntry(null); refresh(); }}
                    onError={onError}
                />
            )}
        </section>
    );
};

const EditPointModal = ({ entry, meetings, onClose, onSaved, onError }) => {
    const [points, setPoints] = useState(String(entry.points ?? ''));
    const [meetingId, setMeetingId] = useState(entry.meeting?._id || entry.meeting || '');
    const [note, setNote] = useState(entry.note || '');
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        const num = Number(points);
        if (!Number.isFinite(num)) { onError('Points must be a number'); return; }
        try {
            setSaving(true);
            await axiosInstance.put(`/api/meetings/points/${entry._id}`, {
                points: num,
                meetingId: meetingId || undefined,
                note: note.trim()
            });
            onSaved();
        } catch (e) {
            onError(e.response?.data?.message || 'Could not update points');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="wm-modal-overlay" onClick={onClose}>
            <div className="wm-modal" onClick={(e) => e.stopPropagation()}>
                <div className="wm-modal-head">
                    <h3>Edit points for {entry.user?.fullName || entry.user?.username || 'user'}</h3>
                    <button className="wm-icon-btn" onClick={onClose}><HiOutlineX size={18} /></button>
                </div>
                <form onSubmit={submit}>
                    <label className="wm-label">Meeting</label>
                    <select value={meetingId} onChange={(e) => setMeetingId(e.target.value)}>
                        <option value="">No specific meeting</option>
                        {meetings.map((m) => <option key={m._id} value={m._id}>{meetingLabel(m)}</option>)}
                    </select>
                    <label className="wm-label">Points</label>
                    <input type="number" value={points} onChange={(e) => setPoints(e.target.value)} />
                    <label className="wm-label">Note (optional)</label>
                    <input type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason / criteria" />
                    <div className="wm-modal-actions">
                        <button type="button" className="wm-btn-secondary" onClick={onClose}>Cancel</button>
                        <button type="submit" className="wm-btn-primary" disabled={saving}>
                            {saving ? 'Saving…' : 'Save changes'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default WeeklyMeetings;
